import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  BookOpen,
  Download,
  FilePlus2,
  FolderOpen,
  Keyboard,
  MoreHorizontal,
  Moon,
  PanelLeft,
  PanelRight,
  Redo2,
  Save,
  ShieldCheck,
  Sun,
  Undo2,
} from 'lucide-react';
import { useStore, DOC_KEY } from '@/state/store';
import { Button, IconButton } from '@/components/ui/Button';
import { bundleDoc, openBundle } from '@/state/persistence';
import { pickFile } from '@/media/pick';
import { downloadBlob, safeName } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { useIsCompact } from '@/hooks/useMediaQuery';
import { ToolSwitcher } from '@/components/tools/ToolSwitcher';

export function TopBar() {
  const name = useStore((s) => s[DOC_KEY[s.tool]].name);
  const rename = useStore((s) => s.rename);
  const undo = useStore((s) => s.undo);
  const redo = useStore((s) => s.redo);
  const canUndo = useStore((s) => s.canUndo());
  const canRedo = useStore((s) => s.canRedo());
  const ui = useStore((s) => s.ui);
  const setUi = useStore((s) => s.setUi);
  const toast = useStore((s) => s.toast);
  const loadDoc = useStore((s) => s.loadDoc);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const compact = useIsCompact();

  // As overlay drawers the two panels would stack on top of each other, so on
  // compact screens opening one closes the other.
  const togglePanel = (side: 'left' | 'right') => {
    const open = side === 'left' ? !ui.leftOpen : !ui.rightOpen;
    if (side === 'left') setUi({ leftOpen: open, rightOpen: compact ? false : ui.rightOpen });
    else setUi({ rightOpen: open, leftOpen: compact ? false : ui.leftOpen });
  };

  const saveFile = async () => {
    const state = useStore.getState();
    const blob = await bundleDoc(state.tool, state.activeDoc());
    downloadBlob(blob, `${safeName(name)}.collage`);
    toast('Project file saved', 'success');
  };

  const openFile = async () => {
    const file = await pickFile('.collage,.collagen,application/json');
    if (!file) return;
    try {
      const { tool: fileTool, doc } = await openBundle(file);
      loadDoc(fileTool, doc);
      toast('Project opened', 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not open file', 'error');
    }
  };

  // The chooser doubles as the "new document" dialog: it is the one place
  // that explains the three tools, and it makes clear that starting one over
  // leaves the other two alone.
  const startNew = () => setUi({ chooserOpen: 'modal' });

  const toggleTheme = () => setUi({ theme: ui.theme === 'dark' ? 'light' : 'dark' });

  return (
    <header className="panel z-40 flex h-12 shrink-0 items-center gap-1.5 px-2 sm:gap-2 sm:px-3 hairline-b">
      <img
        src="/icon-512.png"
        alt=""
        width={32}
        height={32}
        className="h-8 w-8 shrink-0 rounded-[8px] border border-[var(--hairline)] object-cover"
      />
      <span className="brand-wordmark mr-1 hidden lg:block">
        nextcollage
      </span>

      <div className="mx-1 hidden h-5 w-px bg-[var(--hairline)] sm:block" />

      <ToolSwitcher />

      <div className="mx-1 hidden h-5 w-px bg-[var(--hairline)] sm:block" />

      <input
        ref={inputRef}
        value={name}
        onChange={(e) => rename(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.blur()}
        aria-label="Project name"
        className={cn(
          'h-7 min-w-0 flex-1 rounded-[7px] bg-transparent px-2 text-[13px] font-medium outline-none',
          'transition hover:bg-[var(--bg-hover)] focus:bg-[var(--bg-sunken)]',
          'focus:shadow-[inset_0_0_0_1px_var(--accent)] sm:w-[170px] sm:flex-none',
        )}
      />

      <div className="flex shrink-0 items-center gap-0.5">
        <IconButton label="Undo (⌘Z)" onClick={undo} disabled={!canUndo}>
          <Undo2 size={15} />
        </IconButton>
        <IconButton label="Redo (⇧⌘Z)" onClick={redo} disabled={!canRedo}>
          <Redo2 size={15} />
        </IconButton>
      </div>

      <div className="hidden flex-1 sm:block" />

      {/* File actions collapse into the overflow menu on small screens. */}
      <div className="hidden shrink-0 items-center gap-0.5 md:flex">
        <IconButton label="New document" onClick={startNew}>
          <FilePlus2 size={15} />
        </IconButton>
        <IconButton label="Open project file" onClick={openFile}>
          <FolderOpen size={15} />
        </IconButton>
        <IconButton label="Save project file" onClick={saveFile}>
          <Save size={15} />
        </IconButton>
        <div className="mx-1 h-5 w-px bg-[var(--hairline)]" />
      </div>

      {/* Panel toggles are redundant on phones — the bottom bar owns them. */}
      <div className="hidden shrink-0 items-center gap-0.5 sm:flex">
        <IconButton
          label="Toggle photo panel"
          active={ui.leftOpen}
          onClick={() => togglePanel('left')}
        >
          <PanelLeft size={15} />
        </IconButton>
        <IconButton
          label="Toggle inspector"
          active={ui.rightOpen}
          onClick={() => togglePanel('right')}
        >
          <PanelRight size={15} />
        </IconButton>
        <IconButton
          label={ui.theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
          onClick={toggleTheme}
        >
          {ui.theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </IconButton>
        <IconButton
          label="Keyboard shortcuts (?)"
          className="hidden lg:inline-flex"
          onClick={() => setUi({ shortcutsOpen: true })}
        >
          <Keyboard size={15} />
        </IconButton>
      </div>

      <OverflowMenu
        className="md:hidden"
        items={[
          { label: 'New document', icon: <FilePlus2 size={14} />, onClick: startNew },
          { label: 'Open project file', icon: <FolderOpen size={14} />, onClick: openFile },
          { label: 'Save project file', icon: <Save size={14} />, onClick: saveFile },
          {
            label: ui.theme === 'dark' ? 'Light appearance' : 'Dark appearance',
            icon: ui.theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />,
            onClick: toggleTheme,
          },
          { label: 'Collage guide', icon: <BookOpen size={14} />, href: '/guide' },
          { label: 'Privacy', icon: <ShieldCheck size={14} />, href: '/privacy' },
        ]}
      />

      <Button
        variant="primary"
        icon={<Download size={13} />}
        onClick={() => setUi({ exportOpen: true })}
        className="ml-1 hidden shrink-0 sm:inline-flex"
      >
        Export
      </Button>
    </header>
  );
}

interface MenuItem {
  label: string;
  icon: ReactNode;
  /** Menu entries are either an action or a link to one of the static pages. */
  onClick?: () => void;
  href?: string;
}

function OverflowMenu({ items, className }: { items: MenuItem[]; className?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={cn('relative shrink-0', className)}>
      <IconButton label="More actions" active={open} onClick={() => setOpen((v) => !v)}>
        <MoreHorizontal size={16} />
      </IconButton>
      {open && (
        <div
          role="menu"
          className={cn(
            'animate-pop absolute right-0 top-[calc(100%+6px)] z-50 w-[210px] overflow-hidden',
            'rounded-[12px] bg-[var(--bg-panel-solid)] p-1 shadow-[var(--shadow-lg)]',
            'ring-1 ring-[var(--hairline)]',
          )}
        >
          {items.map((item) => {
            const cls = cn(
              'flex w-full items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-left text-[13px]',
              'text-[var(--text-primary)] no-underline transition hover:bg-[var(--bg-hover)]',
            );
            const body = (
              <>
                <span className="text-[var(--text-secondary)]">{item.icon}</span>
                {item.label}
              </>
            );
            return item.href ? (
              <a key={item.label} role="menuitem" href={item.href} className={cls}>
                {body}
              </a>
            ) : (
              <button
                key={item.label}
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  item.onClick?.();
                }}
                className={cls}
              >
                {body}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
